<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-swap-horizontal"
                :title="isDeletePage() != true ? 'Stock Logs' : 'Deleted Stock Logs'"
                subtitle="Every stock addition and removal, with who did it and why"
                style="width: 100%"
              >
                <v-btn
                  v-if="isDeletePage() != true"
                  outlined
                  color="primary"
                  :loading="isExcelLoading"
                  @click="generateExcel()"
                >
                  <v-icon small class="mr-1">mdi-microsoft-excel</v-icon>
                  Generate Excel
                </v-btn>
              </AppPageHeader>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Summary Cards -->
      <v-row v-if="!isDeletePage()">
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="0" style="border:1px solid #e0e0e0" class="fill-height">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text">Company Stock In</div>
                <div class="text-h4 font-weight-bold success--text">{{ summaryData.company_in }}</div>
                <div class="text-caption mt-1 grey--text">Purchases, additions, cancels/returns</div>
              </div>
              <v-avatar size="48" color="success lighten-4">
                <v-icon color="success" size="26">mdi-plus-circle-outline</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card elevation="0" style="border:1px solid #e0e0e0" class="fill-height">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text">Company Stock Out</div>
                <div class="text-h4 font-weight-bold error--text">{{ summaryData.company_out }}</div>
                <div class="text-caption mt-1 grey--text">Given to sellers, sold, removed</div>
              </div>
              <v-avatar size="48" color="error lighten-4">
                <v-icon color="error" size="26">mdi-minus-circle-outline</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card elevation="0" style="border:1px solid #e0e0e0" class="fill-height">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text">Company Stock Change</div>
                <div class="text-h4 font-weight-bold indigo--text">{{ summaryData.company_net }}</div>
                <div class="text-caption mt-1 grey--text">In &minus; Out</div>
              </div>
              <v-avatar size="48" color="indigo lighten-4">
                <v-icon color="indigo" size="26">mdi-package-variant-closed</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card elevation="0" style="border:1px solid #e0e0e0" class="fill-height">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text">Sold by Sellers</div>
                <div class="text-h4 font-weight-bold purple--text">{{ summaryData.seller_sold }}</div>
                <div class="text-caption mt-1 grey--text">From their own stock, not company stock</div>
              </div>
              <v-avatar size="48" color="purple lighten-4">
                <v-icon color="purple" size="26">mdi-account-cash-outline</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row>
        <v-col md="12">
          <v-card elevation="0" style="border:1px solid #e0e0e0">
            <v-card-text>
              <div class="text-overline grey--text mb-1">
                <v-icon small class="mr-1">mdi-filter-variant</v-icon>Filters
              </div>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    outlined
                    dense
                    v-model="filter.search_keyword"
                    label="Search ... "
                    prepend-inner-icon="mdi-magnify"
                    hide-details
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="2">
                  <v-autocomplete
                    outlined
                    dense
                    ref="variant"
                    v-model="filter.variant_id"
                    :items="variants"
                    label="Product Variant"
                    hide-details
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-package-variant"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="2">
                  <v-autocomplete
                    outlined
                    dense
                    ref="action_type"
                    v-model="filter.action_type"
                    :items="actionTypes"
                    label="Action Type"
                    hide-details
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-swap-horizontal"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="2">
                  <v-autocomplete
                    ref="status"
                    outlined
                    dense
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    hide-details
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-toggle-switch"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="2">
                  <v-autocomplete
                    outlined
                    dense
                    ref="created_by"
                    v-model="filter.created_by_id"
                    :items="users"
                    label="Created By"
                    hide-details
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-account"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="500"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-start">Sr No</th>
                      <th class="text-start">Status</th>
                      <th class="text-start">Product</th>
                      <th class="text-start">Variant</th>
                      <th class="text-start">Quantity</th>
                      <th class="text-start">Action Type</th>
                      <th class="text-start">Stock Of</th>
                      <th class="text-start">Created By</th>
                      <th class="text-start">Date</th>
                      <th class="text-start">Remarks</th>
                    </tr>
                  </thead>
                  <tbody v-if="stockLogs.length != 0">
                    <tr v-for="(log, i) in stockLogs" :key="`log${i}`">
                      <td style="min-width: 85px">
                        {{ serialNoStarting + i + 1 }}
                      </td>
                      <td>
                        <v-tooltip bottom v-if="log.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>Stock Log is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="log.is_active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>Stock Log is InActive</span>
                        </v-tooltip>
                      </td>
                      <td>{{ log.variant && log.variant.product ? log.variant.product.name : "" }}</td>
                      <td>{{ log.variant ? `${log.variant.color} - ${log.variant.size}` : "" }}</td>
                      <td>
                        <v-chip
                          :color="getQuantityColor(log)"
                          text-color="white"
                          small
                        >
                          {{ getQuantityPrefix(log) }}{{ log.quantity || 0 }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip
                          :color="getActionTypeColor(log)"
                          text-color="white"
                          small
                        >
                          {{ log.action_label || getActionTypeLabel(log.action_type) }}
                        </v-chip>
                      </td>
                      <td>
                        <span :class="log.stock_pool === 'company' ? '' : 'purple--text font-weight-medium'">
                          {{ log.stock_of || "Company Stock" }}
                        </span>
                      </td>
                      <td>{{ log.created_by ? log.created_by.user_name : "" }}</td>
                      <td>{{ log.created_at | niceDate }}</td>
                      <td>
                        <v-tooltip bottom v-if="log.remarks">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                              {{ truncateText(log.remarks, 30) }}
                            </span>
                          </template>
                          <span>{{ log.remarks }}</span>
                        </v-tooltip>
                      </td>

                      <!-- <td class="text-right">
                        <template v-if="isDeletePage() != true">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="error"
                                @click="Delete_this(log.id)"
                              >
                                <v-icon dark> mdi-delete </v-icon>
                              </v-btn>
                            </template>
                            <span>Delete this stock log</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="ml-2"
                                fab
                                x-small
                                color="primary"
                                :to="`/stock_logs/${encryptIt(log.id)}`"
                              >
                                <v-icon dark> mdi-pencil </v-icon>
                              </v-btn>
                            </template>
                            <span>Edit this stock log</span>
                          </v-tooltip>
                        </template>
                        <template v-else>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="error"
                                @click="Restore_this(log.id)"
                              >
                                <v-icon dark> mdi-delete-restore </v-icon>
                              </v-btn>
                            </template>
                            <span>Restore this stock log</span>
                          </v-tooltip>
                        </template>
                      </td> -->
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="11" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- Delete Success Dialog Box -->
      <v-dialog v-model="is_deleted_log" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Stock log deleted successfully.</h3>
                      <br />
                      <v-btn color="success" @click="is_deleted_log = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_log" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Stock log restored successfully.</h3>
                      <br />
                      <v-btn color="success" @click="is_restored_log = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import moment from "moment";
import AppPageHeader from "@/components/AppPageHeader.vue";

export default {
  name: "stockLogsPage",
  components: { AppPageHeader },
  data() {
    return {
      filter: {
        search_keyword: "",
        variant_id: "",
        action_type: "",
        created_by_id: "",
        is_active: "",
        show_deleted: false,
      },
      page: 1,
      serialNoStarting: 0,
      paginationLength: 0,
      count: 0,
      is_deleted_log: false,
      restore_log: false,
      is_restored_log: false,
      stockLogs: [],
      statuses: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      actionTypes: [
        { value: "", text: "Select Action Type" },
        { value: 1, text: "Stock In" },
        { value: 2, text: "Stock Out" },
        { value: 3, text: "Given to Distributor" },
        { value: 4, text: "Given to Seller" },
        { value: 5, text: "Given to Franchise" },
        { value: 6, text: "Sale (Online / Counter / Seller)" },
        { value: 7, text: "Back in Stock (Cancel / Return)" },
      ],
      variants: [{ value: "", text: "Select Product Variant" }],
      users: [{ value: "", text: "Select User" }],
      isLoading: false,
      isExcelLoading: false,
      // Calculated by the API over every log matching the filters
      summaryData: this.emptySummary(),
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
    this.getMasters();
  },
  methods: {
    emptySummary() {
      return { company_in: 0, company_out: 0, company_net: 0, seller_sold: 0, seller_returned: 0 };
    },
    async getMasters() {
      try {
        // Fetch products with variants
        const productsResponse = await axios.get('/products');
        if (productsResponse.data && productsResponse.data.data) {
          productsResponse.data.data.forEach((product) => {
            if (product.product_variants) {
              product.product_variants.forEach((variant) => {
                this.variants.push({
                  value: variant.id,
                  text: `${product.name} - ${variant.color} - ${variant.size}`,
                });
              });
            }
          });
        }

        // Fetch users
        const usersResponse = await axios.get('/users');
        if (usersResponse.data && usersResponse.data.data) {
          usersResponse.data.data.forEach((user) => {
            this.users.push({
              value: user.id,
              text: user.name || user.user_name || `${user.first_name} ${user.last_name}`,
            });
          });
        }
      } catch (error) {
        // Handle error silently
      }
    },
    async getData() {
      // Get Data
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      this.filter.variant_id = this.filter.variant_id
        ? this.filter.variant_id
        : "";
      this.filter.action_type = this.filter.action_type
        ? this.filter.action_type
        : "";
      this.filter.created_by_id = this.filter.created_by_id
        ? this.filter.created_by_id
        : "";
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&variant_id=" +
        this.filter.variant_id +
        "&action_type=" +
        this.filter.action_type +
        "&created_by_id=" +
        this.filter.created_by_id +
        "&search_keyword=" +
        this.filter.search_keyword;
      await axios
        .get(`stock_logs?${query}`)
        .then((response) => {
          this.stockLogs = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);

          // Calculate summary after data is fetched
          this.summaryData = { ...this.emptySummary(), ...(response.data.summary || {}) };
        })
        .catch(function () {
          // Handle error silently
        });

      this.isLoading = false;
    },
    async Delete_this(id) {
      this.delete_log = true;
      await axios
        .post(`/stock_logs/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_log = false;
          this.is_deleted_log = true;
          this.getData();
        })
        .catch(function () {
          // Handle error silently
        });
    },
    async generateExcel() {
      this.isExcelLoading = true;
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      this.filter.variant_id = this.filter.variant_id
        ? this.filter.variant_id
        : "";
      this.filter.action_type = this.filter.action_type
        ? this.filter.action_type
        : "";
      this.filter.created_by_id = this.filter.created_by_id
        ? this.filter.created_by_id
        : "";
      let query =
        "is_active=" +
        this.filter.is_active +
        "&variant_id=" +
        this.filter.variant_id +
        "&action_type=" +
        this.filter.action_type +
        "&created_by_id=" +
        this.filter.created_by_id +
        "&search_keyword=" +
        this.filter.search_keyword;
      await axios
        .get(`/stock_logs/excel?${query}`, { responseType: 'blob' })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `stock_logs_${moment().format('YYYY_MM_DD_HHmmss')}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(function () {
          // Handle error silently
        });
      this.isExcelLoading = false;
    },
    isDeletePage() {
      let response = false;
      if (this.$route.query.open_trash) {
        response = true;
      }
      return response;
    },
    async Restore_this(id) {
      this.restore_log = true;
      await axios
        .post(`/stock_logs/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.restore_log = false;
          this.is_restored_log = true;
          this.getData();
        })
        .catch(function () {
          // Handle error silently
        });
    },
    formatDate(date) {
      return date ? moment(date).format("YYYY-MM-DD HH:mm") : "";
    },
    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    // In/out comes from the API (StockLog::describe) - a seller's return goes
    // back into their stock, so the action type alone can't tell
    isStockIn(log) {
      return log.direction ? log.direction === "in" : [1, 7].includes(log.action_type);
    },
    getQuantityPrefix(log) {
      return this.isStockIn(log) ? "+" : "-";
    },
    getQuantityColor(log) {
      return this.isStockIn(log) ? "green" : "red";
    },
    getActionTypeColor(log) {
      // Sales from a seller's own stock stand apart from company movements
      if (log.action_type === 6 && log.stock_pool === "seller") return "deep-purple";
      const colors = {
        1: "green",
        2: "red",
        3: "blue",
        4: "purple",
        5: "orange",
        6: "indigo",
        7: "teal",
      };
      return colors[log.action_type] || "grey";
    },
    getActionTypeLabel(actionType) {
      const labels = {
        1: "Stock In",
        2: "Stock Out",
        3: "Given to Distributor",
        4: "Given to Seller",
        5: "Given to Franchise",
        6: "Sale",
        7: "Back in Stock (Cancel / Return)",
      };
      return labels[actionType] || "Unknown";
    },
  },
};
</script>